import { MyEvolvedPlayersList } from "@/components/organisms/PlayersList/MyEvolvedPlayersList";
import { PlayersToolbar } from "@/components/organisms/PlayersToolbar/PlayersToolbar";
import { RoleSelectSheetContent } from "@/design-system/RoleSelectPanel/RoleSelectPanel";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import {
  PlayerRole,
  defaultPositions,
  playersSelector,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { userIsLoggedInSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import type { PlayersSortByType } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import {
  evolvedPlayersSelector,
  getMyEvolutionPlayers,
} from "@repo/shared/redux";
import { buildRoleGroups } from "@repo/shared/utils";
import { matchesSearch } from "@repo/shared/utils";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { StickySidebar } from "../../Players/StickySidebar";
import { MyEvolvedTabs } from "./MyEvolvedTabs";

export const EvolvedPlayersPage = () => {
  const { t } = useTranslation(["evolutions", "players"]);
  const [playerRole, setPlayerRole] = useState<PlayerRole>();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<PlayersSortByType>("meta-rating");
  const [playerRoleOpen, setPlayerRoleOpen] = useState(false);
  const { myEvolvedPlayers, myEvolvedPlayersStatus } = useSelector(
    evolvedPlayersSelector,
  );
  const { playerRoles } = useSelector(playersSelector);
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const isLoggedIn = useSelector(userIsLoggedInSelector);
  const refPlayerRole = useOutsideClick(() => setPlayerRoleOpen(false));

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!isLoggedIn) return;
    dispatch(
      getMyEvolutionPlayers({ sort: sortBy, playerRoleId: playerRole?.id }),
    );
  }, [sortBy, playerRole]);

  const filteredPlayers = myEvolvedPlayers.filter((p) =>
    matchesSearch(p.name, searchTerm),
  );

  return (
    <>
      <PlayersToolbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleToolbarAction={() => setPlayerRoleOpen((prev) => !prev)}
        isMobile={isMobile}
        hasFilters={false}
        sortBy={sortBy}
        setSortBy={setSortBy}
        playerRole={playerRole}
        pageType="my-evolutions"
        quickFilters={<MyEvolvedTabs />}
      />
      {playerRoleOpen && (
        <StickySidebar handleClose={() => setPlayerRoleOpen(false)}>
          <div ref={refPlayerRole} className="pt-4">
            <RoleSelectSheetContent
              groups={buildRoleGroups({
                playerRoles,
                positionsOrder: defaultPositions.map(({ label }) => label),
              })}
              selectedRoleId={playerRole?.id}
              subtitle={t("players:roleSelect.subtitle")}
              onPick={(role) => {
                setPlayerRole(playerRoles.find((r) => r.id === role?.id));
                setPlayerRoleOpen(false);
              }}
              onClose={() => setPlayerRoleOpen(false)}
            />
          </div>
        </StickySidebar>
      )}
      <div className="mx-auto flex min-h-[60vh] w-full max-w-content flex-col gap-y-4 px-3 pt-3 pb-24 large:px-16 touch-device:px-4">
        <MyEvolvedPlayersList
          players={filteredPlayers}
          status={myEvolvedPlayersStatus}
        />
      </div>
    </>
  );
};
